import { getItem, setItem as setSessionStorageItem } from "~/es6/sessionstorage";
import { hasItem, removeItem, setItem } from "~/util/cookies";
import { ELoginState, IUserInfo } from "~/state/user/interfaces";
import { subscriptionsMe, subscriptionsMeCurrent, login } from "~/util/providers/share-position/subscriptions";
import { setInvitations, setSubscriptions } from "~/state/share-position/state";
import { store } from "~/state/store";
import { _ } from "~/i18n";
import { IInvitation, ISubscriptionUpdate, ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import { selectSubscriptions } from "~/state/share-position/selectors";
import { invitationsMe } from "~/util/providers/share-position/invitations";

export function setSEO() {
	Mapy.SEO.set(_("sharePosition.seo"));
}

// po jake dobe bez dat si zaciname myslet, ze mame jen pravdepodobna cisla
export const STALE_TIMEOUT = 5 * 60000;

const SLOW_UPDATE = 15 * 60 * 1000;
const FAST_UPDATE = 10 * 1000;

let updatePositionsInterval = 0;

// zde si zapamatujeme posledni timestampu, na kterou jsme se ptali
let timestamp = "";

export async function fakeLogin(domain) {
	const sds = getItem("share-sds");

	// tady zkontrolujeme pritomnost sessionStorage polozky share-sds
	if (sds) {
		if (!hasItem("sds")) {
			setItem("sds", sds, Infinity, null, domain);
		}
	// je treba se teprve prihlasit
	} else {
		const { sessionId } = await login();

		setSessionStorageItem("share-sds", sessionId);
		setItem("sds", sessionId, Infinity, null, domain);
	}
}


export function mergeSubscriptionWithUpdate(subscription: ISubscriptionWithDescription, update: ISubscriptionUpdate): ISubscriptionWithDescription {
	const oldRouteParts = subscription.routeParts?.length ? subscription.routeParts : [];
	const newRouteParts = update.routeParts?.length
		? update.routeParts
		: update.routePartsTime > timestamp
			? []
			: oldRouteParts;

	return {
		...subscription,
		...update,
		routeInfo: newRouteParts.length
			? update.routeInfo
				? {
					...subscription.routeInfo ? subscription.routeInfo : {},
					...update.routeInfo,
				}
				: subscription.routeInfo || null
			: null,
		routeParts: newRouteParts.map(routePart => {
			const id = routePart.poi.source === "coor" ? `${routePart.poi.lon},${routePart.poi.lat}` : routePart.poi.id;
			const oldRoutePart = oldRouteParts.find(old => routePart.poi.source === old.poi.source && id === old.poi.id);

			return {
				...routePart,
				poi: {
					...oldRoutePart
						? oldRoutePart.poi
						: {
							description: {
								title: "",
								source: routePart.poi.source,
								id: typeof routePart.poi.id === "number" ? routePart.poi.id : 0,
								mark: {
									lon: routePart.poi.lon,
									lat: routePart.poi.lat,
								},
							},
						},
					...routePart.poi,
					id,
				},
			};
		}),
	};
}

async function updateSharedPositions() {
	const meCurrent = await subscriptionsMeCurrent(timestamp);
	const { subscriptions: current, timestamp: newTimestamp } = meCurrent;
	const state = store.getState();
	const subscriptions = selectSubscriptions(state);
	const updatedSubscriptions: Array<ISubscriptionWithDescription> = [];
	let needsFullUpdate = false;

	current.forEach(updatedSubscription => {
		const subscription = subscriptions.find(sub => sub.subscriptionId === updatedSubscription.subscriptionId);

		// kdyz to neni v novych datech, tak posleme zpatky puvodni zaznam
		if (subscription) {
			// pokud update plati, tak vratime kombinaci s puvodni subscription
			updatedSubscriptions.push(mergeSubscriptionWithUpdate(subscription, updatedSubscription));
		} else {
			needsFullUpdate = true;
		}
	});

	// eslint-disable-next-line require-atomic-updates
	timestamp = newTimestamp;
	// hned dispatchneme update pozic
	store.dispatch(setSubscriptions(updatedSubscriptions));

	// zjistime, jestli vsechno z prvniho, je ve druhem a obracene
	if (!needsFullUpdate) {
		subscriptions.forEach(subscription => {
			const found = current.find(update => update.subscriptionId === subscription.subscriptionId);

			if (!found) {
				needsFullUpdate = true;
			}
		});
	}

	// pokud nechybi nic v prvnim, musime jeste kouknout, jestli to neni naopak
	if (!needsFullUpdate) {
		current.forEach(update => {
			const found = subscriptions.find(subscription => update.subscriptionId === subscription.subscriptionId);

			if (!found) {
				needsFullUpdate = true;
			}
		});
	}

	// overime, kolik POIu v routeParts ma title === "", tedy v updatu prislo neco, co nebylo v uvodnim nacteni a je treba prenacist
	if (!needsFullUpdate) {
		updatedSubscriptions.forEach(subscription => {
			if (subscription.routeParts?.length) {
				if (!subscription.routeParts[subscription.routeParts.length - 1].poi?.description?.title) {
					needsFullUpdate = true;
				}
			}
		});
	}

	// v jednom nebo druhem prohledavani se stalo, ze nejake id zustalo plonkove a tedy nesedi ta pole k sobe, pripadne se zmenila za behu nejaka trasa a my nezname jmeno cile -> je treba updatovat cely seznam
	if (needsFullUpdate) {
		try {
			const subs = await subscriptionsMe();

			// eslint-disable-next-line require-atomic-updates
			timestamp = subs.timestamp;
			store.dispatch(setSubscriptions(subs.subscriptions));
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.log(ex);
			// eslint-disable-next-line require-atomic-updates
			timestamp = "";
		}
	}
}

export async function initSharedPositions(user: IUserInfo, domain: string): Promise<Array<ISubscriptionWithDescription>> {
	if (user.state === ELoginState.Green) {
		// smazeme cookie, pokud byla, protoze jinak by se nam bila s opravovym prihlasenim
		removeItem("sds");
	} else {
		// kdyz uzivatel neni, tak doplnime sds cookie bud z BE nebo ze sessionStorage, pokud tam uz nejaka je
		await fakeLogin(domain);
	}

	// pokud se updatuje, tak to prestaneme delat a teprve se uvidi, jestli se to znovu delat zacne
	if (updatePositionsInterval) {
		clearInterval(updatePositionsInterval);
		updatePositionsInterval = 0;
	}

	// pak zkusime nacist seznam sledovanych
	try {
		const { subscriptions, timestamp: newTimestamp } = await subscriptionsMe();

		timestamp = newTimestamp;
		store.dispatch(setSubscriptions(subscriptions));

		// pokud se to povedlo, tak teprve ma cenu nahodit opakovane ptani BE
		if (updatePositionsInterval === 0) {
			updatePositionsInterval = window.setInterval(updateSharedPositions, subscriptions.length > 0 ? FAST_UPDATE : SLOW_UPDATE);
		}

		return subscriptions;
	} catch (ex) {
		// eslint-disable-next-line no-console
		console.log(ex);

		return [];
	}
}

export async function loadInvitations(user: IUserInfo) {
	// pokud je ale novy uzivatel prihlaseny, nacteme jeho pozvanky
	if (user.state === ELoginState.Green) {
		try {
			const invitationsResponse = await invitationsMe();

			if (Array.isArray(invitationsResponse?.items)) {
				store.dispatch(setInvitations(invitationsResponse.items));
			}
		} catch (ex) {
			// pri neuspechu proste tise selzeme a nemame pozvanky nactene, dokud se o to znovu uzivatel nepokusi sam
		}
	}
}

function calculateSegment(first: number, second: number, segment: number): number {
	const diff = second - first;

	return first + (diff * segment);
}

export function routeParts2SMapCoords(routeParts: ISubscriptionWithDescription["routeParts"], routeIndex: number, segmentIndex: number): Array<Array<any>> /* Array<SMap.Coords> */ {
	const coordsGone = [];
	const coordsToGo = [];

	if (routeParts?.length) {
		routeParts.forEach((routePart, index) => {
			if (routePart.geometry) {
				try {
					// celou routePart uz projel
					if (index < routeIndex) {
						coordsGone.push(...SMap.Coords.stringToCoords(routePart.geometry));
					// celou routePart teprve pojede
					} else if (index > routeIndex) {
						coordsToGo.push(...SMap.Coords.stringToCoords(routePart.geometry));
					// v tehle routePart zrovna jede
					} else {
						const coords = SMap.Coords.stringToCoords(routePart.geometry);

						coordsGone.push(...coords.slice(0, Math.floor(segmentIndex) + 1));
						coordsToGo.push(...coords.slice(Math.floor(segmentIndex) + 1));

						/*
						segmentIndex je ve skutecnosti float a je nekde mezi poslednim gone a prvnim togo,
						takze si musime vypocitat souradnici mezi nimi v pomeru danem tim indexem a ten vlozit na konec gone a zacatek togo
						*/
						const goneLast = coordsGone.at(-1)?.toWGS84();
						const goToFirst = coordsToGo.at(0)?.toWGS84();

						if (goneLast && goToFirst) {
							const segmentRest = segmentIndex % 1;
							const segmentCoords = SMap.Coords.fromWGS84(calculateSegment(goneLast[0], goToFirst[0], segmentRest), calculateSegment(goneLast[1], goToFirst[1], segmentRest));

							coordsGone.push(segmentCoords.clone());
							coordsToGo.unshift(segmentCoords.clone());
						}
					}
				} catch (ex) {
					//
				}
			}
		});
	}

	return [coordsGone, coordsToGo];
}

export function createDeviceIcon(number: number) {
	return `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56">
		<style>
			text {
				font-family: Arial, sans-serif;
				font-size: 14px;
				font-weight: 700;
			}
		</style>
		<rect width="56" height="56" x="0" y="0" fill="#ebedf0" />
		<path fill="#5f7182" d="M25.451 42.306h5.098a.806.806 0 0 0 .59-.25.8.8 0 0 0 .25-.584.8.8 0 0 0-.25-.583.807.807 0 0 0-.59-.25h-5.098a.807.807 0 0 0-.59.25.8.8 0 0 0-.25.583.8.8 0 0 0 .25.584c.167.166.364.25.59.25Zm-6.34 4.027c-.75 0-1.4-.275-1.951-.826-.551-.55-.826-1.201-.826-1.951V12.444c0-.75.275-1.4.826-1.95.55-.552 1.201-.827 1.951-.827H36.89c.75 0 1.4.275 1.951.826.551.551.827 1.201.827 1.951v31.112c0 .75-.276 1.4-.827 1.951-.55.55-1.2.826-1.95.826H19.11Zm0-9.722H36.89v-20H19.111v20Z"/>
		<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#5f7182">${number}</text>
	</svg>`)}`;
}


export function createMapDeviceIcon(number: number) {
	return `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
		<style>
			text {
				font-family: Arial, sans-serif;
				font-size: 14px;
				font-weight: 700;
			}
		</style>
		<rect width="40" height="40" fill="#ebedef" />
		<path fill="#5f7182" d="M17.45 41.306h5.099a.807.807 0 0 0 .59-.25.8.8 0 0 0 .25-.584.8.8 0 0 0-.25-.583.807.807 0 0 0-.59-.25h-5.098a.807.807 0 0 0-.59.25.8.8 0 0 0-.25.583.8.8 0 0 0 .25.584c.167.166.363.25.59.25Zm-6.339 4.027c-.75 0-1.4-.275-1.951-.826-.551-.551-.827-1.201-.827-1.951V11.444c0-.75.276-1.4.827-1.951.55-.55 1.201-.826 1.951-.826h17.778c.75 0 1.4.275 1.951.826.551.55.827 1.201.827 1.951v31.111c0 .75-.276 1.401-.827 1.952-.55.55-1.201.826-1.951.826H11.11Zm0-9.722h17.778v-20H11.11v20Z"/>
		<text x="50%" y="27" dominant-baseline="middle" text-anchor="middle" fill="#5f7182">${number}</text>
	</svg>`)}`;
}

/**
 * Z pole sledovaných poloh vybere jen ty, které dle zadání chceme nějak vizualizovat.
 */
export function filterSubscriptions(subscriptions: Array<ISubscriptionWithDescription>): Array<ISubscriptionWithDescription> {
	if (!Array.isArray(subscriptions)) {
		return [];
	}

	return subscriptions.filter(subscription => subscription.sharingType !== "device" || subscription.sharingStatus !== "canceled");
}

/**
 * Z pole sdílených pozvánek na sledování polohy vybere jen ty, které dle zadání chceme nějak vizualizovat.
 */
export function filterInvitations(invitations: Array<IInvitation>): Array<IInvitation> {
	if (!Array.isArray(invitations)) {
		return [];
	}

	return invitations.filter(invitation => invitation.invitationStatus !== "expired" && invitation.invitationStatus !== "canceled" && (!invitation.sharing || invitation.sharing.sharingType !== "device"));
}
